import { useCallback, useEffect, useRef, useState } from 'react';
import { druckenWennBereit } from '@/lib/druckBereit';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { SafariUebergabe } from '@/components/druck/SafariUebergabe';
import { useToast } from '@/lib/toast';
import { istIosHomeApp } from '@/lib/iosDruck';
import { useZurueck } from '@/hooks/useZurueck';
import { TeilenDruck } from '@/components/druck/TeilenDruck';
import { blattAlsPdf } from '@/lib/druckTeilen';
import { Spinner } from '@/components/ui/Spinner';
import type { LieferscheinAntwort, LieferscheinVariante } from '@/types/lieferschein';
import {
  LIEFERSCHEIN_VARIANTEN as VARIANTEN,
  LieferscheinBlatt,
  lieferscheinFehlerText as fehlerText
} from '@/components/druck/LieferscheinBlatt';

// Glomer-Lieferschein — Nachbau der Order-Printer-Vorlagen (28.10.2026).
//
// WARUM HIER UND NICHT IN ORDER PRINTER
//   Order Printer druckt nur aus dem Shopify-Admin heraus; vom Handy aus
//   geht das praktisch nicht. Der Nachbau liest die Bestellung beim Öffnen
//   live aus Shopify (api/lib/shopify_lieferschein.php) und druckt sie
//   von jedem Gerät. Die Order-Printer-Vorlagen bleiben als Rückfallebene.
//
// AUFBAU UND WORTLAUT folgen der Liquid-Vorlage „Lieferschein": Kopf mit
// Shop-Titel links und Nummer/QR rechts, Liefer- und Rechnungsadresse,
// Positionstabelle mit wiederholtem Kopf je Seite, Anmerkungen, Fußzeile.
// Geändert gegenüber der Vorlage, auf Wunsch des Betreibers: QR 35 mm,
// Bestellnummer 20 pt fett.
//
// DRUCKEN wie beim Zettel: Die Seite bleibt nach dem Druckdialog stehen
// (Befund #51 — iOS kehrt aus print() sofort zurück), zurück geht es über
// den sichtbaren Knopf. Gedruckt wird ausschließlich `.lieferschein`
// (index.css, eigener Block — der Zettel-Block bleibt unberührt).

type Suche = { variante?: LieferscheinVariante };

function LieferscheinDruck() {
  const { bestellnr } = Route.useParams();
  const { variante: gewaehlt } = Route.useSearch();
  const variante: LieferscheinVariante = gewaehlt ?? 'lieferschein';
  const navigate = useNavigate();
  const gedruckt = useRef(false);
  // iPhone-Home-Bildschirm-App: dort druckt nur Safari (lib/iosDruck.ts).
  const [iosApp] = useState(istIosHomeApp);

  const q = useQuery({
    queryKey: ['lieferschein', bestellnr, variante],
    queryFn: () =>
      apiFetch<LieferscheinAntwort>(
        `/bestellungen/lieferschein.php?bestellnr=${encodeURIComponent(bestellnr)}` +
          `&variante=${encodeURIComponent(variante)}`
      ),
    // Ein Shopify-Ausfall ist keine Frage von drei Versuchen in Folge;
    // der Knopf „Erneut laden" ist ehrlicher als ein langer Ladekreis.
    retry: false,
    staleTime: 0
  });

  // Vorschau auf schmalen Schirmen: das Blatt (190 mm ≈ 718 px) wird
  // verkleinert statt abgeschnitten. Gemessen am Handy (390 px) lief
  // sonst die rechte Hälfte mit Nummer und QR aus dem Bild. Nur der
  // Bildschirm — im Druck setzt index.css `zoom` zurück auf 1.
  const [massstab, setMassstab] = useState(1);
  useEffect(() => {
    function messen(): void {
      setMassstab(Math.min(1, (window.innerWidth - 32) / 718));
    }
    messen();
    window.addEventListener('resize', messen);
    return () => window.removeEventListener('resize', messen);
  }, []);

  // Ein Schritt zurück statt eines neuen Eintrags (hooks/useZurueck.ts):
  // Sonst stünde die Druckseite hinter der Bestellung im Verlauf, und ein
  // späteres „Zurück" aus der Bestellung öffnete den Druck noch einmal.
  const zurueck = useZurueck({ to: '/bestellungen/$bestellnr', params: { bestellnr } });

  // iPhone-App: Sobald Safari übernommen hat, ersetzt die Bestellung die
  // Druckroute im Verlauf. Wer zurück in die App wechselt, steht auf der
  // Bestellung — nicht auf einer Route, die Safari erneut öffnet.
  const toast = useToast();
  const uebergeben = useCallback(() => {
    toast('success', 'In Safari geöffnet — dort drucken.');
    void navigate({ to: '/bestellungen/$bestellnr', params: { bestellnr }, replace: true });
  }, [navigate, bestellnr, toast]);

  // Einmal von selbst drucken, sobald der erste Lieferschein steht. Ein
  // Wechsel der Variante druckt nicht erneut — wer umschaltet, will
  // erst sehen, was er bekommt.
  const geladen = q.data !== undefined;
  useEffect(() => {
    if (!geladen || gedruckt.current || iosApp) return;
    gedruckt.current = true;
    // Erst wenn QR-Code und Cover geladen sind (lib/druckBereit.ts).
    return druckenWennBereit('.lieferschein-wurzel', 400);
  }, [geladen, iosApp]);

  const qrBild = q.data
    ? `/api/qr.php?d=${encodeURIComponent(`${window.location.origin}/s/${q.data.kopf.nummer}`)}`
    : null;

  // iPhone-App: gedruckt wird über das Teilen-Menü (TeilenDruck);
  // Safari nur noch, wenn jemand den Ausweg wählt.
  const [ueberSafari, setUeberSafari] = useState(false);
  if (iosApp && ueberSafari) {
    return (
      <SafariUebergabe
        bestellnr={bestellnr}
        druckArt="lieferschein"
        variante={variante}
        onZurueck={zurueck}
        onUebergeben={uebergeben}
      />
    );
  }

  return (
    <div className="lieferschein-wurzel min-h-screen bg-white p-4 text-black">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {iosApp ? (
          // Neu aufbereiten, wenn die Vorlage wechselt (key).
          <TeilenDruck
            key={`${variante}-${q.dataUpdatedAt}`}
            bereit={geladen}
            wurzelSelektor=".lieferschein-wurzel"
            erzeugen={() => lieferscheinAlsPdf(q.data?.kopf.name ?? bestellnr)}
            onSafari={() => setUeberSafari(true)}
          />
        ) : (
          <button
            type="button"
            onClick={() => window.print()}
            disabled={!geladen}
            className="min-h-[44px] rounded-md bg-akzent px-4 text-sm text-cream disabled:opacity-50"
          >
            Drucken
          </button>
        )}
        <button
          type="button"
          onClick={zurueck}
          className="min-h-[44px] rounded-md border border-line px-4 text-sm"
        >
          Zurück zur Bestellung
        </button>
        <label className="flex items-center gap-2 text-sm">
          <span>Vorlage</span>
          <select
            value={variante}
            onChange={(e) =>
              void navigate({
                to: '/bestellungen/$bestellnr/lieferschein',
                params: { bestellnr },
                search: { variante: e.target.value as LieferscheinVariante },
                replace: true
              })
            }
            className="min-h-[44px] rounded-md border border-line bg-white px-2 text-base"
          >
            {VARIANTEN.map((v) => (
              <option key={v} value={v}>
                {q.data?.varianten[v] ?? v}
              </option>
            ))}
          </select>
        </label>
        {q.data?.ohne_produkt && (
          <p className="w-full text-xs text-coral">
            Ohne Autor und Cover: Der Shopify-Zugang hat den Bereich „read_products" nicht.
          </p>
        )}
      </div>

      {q.isPending && (
        <div className="flex justify-center py-16">
          <Spinner className="h-8 w-8" />
        </div>
      )}
      {q.isError && (
        <div className="max-w-xl space-y-3">
          <p className="text-coral">{fehlerText(q.error)}</p>
          <button
            type="button"
            onClick={() => void q.refetch()}
            className="min-h-[44px] rounded-md border border-line px-4 text-sm"
          >
            Erneut laden
          </button>
        </div>
      )}

      {q.data && qrBild && (
        // Auf dem Telefon ist A4 breiter als der Schirm: dort darf die
        // Vorschau seitlich rollen. Im Druck gilt die Breite des Blatts.
        <div className="lieferschein-rahmen" style={{ zoom: massstab }}>
          <LieferscheinBlatt daten={q.data} qrBild={qrBild} />
        </div>
      )}
    </div>
  );
}

/**
 * Der Lieferschein als A4-PDF in der Gestalt des Druck-CSS (index.css):
 * 210 mm breit, Ränder 20/25/10 mm, ohne Vorschau-Verkleinerung.
 */
function lieferscheinAlsPdf(name: string): Promise<File> {
  const blatt = document.querySelector<HTMLElement>('.lieferschein');
  if (!blatt) return Promise.reject(new Error('kein Lieferschein'));
  return blattAlsPdf(blatt, `Lieferschein-${name}.pdf`, (klon) => {
    // html2canvas kennt keine Silbentrennung: Ein Wort, breiter als die
    // Namensspalte, bräche mitten im Wort ohne Strich („Waldorfkindergart|
    // en", im Prüfstand gesehen). Dann lieber die Schrift für die Aufnahme
    // verkleinern, bis das längste Wort passt — gemessen im Klon, also in
    // genau der Gestalt, die aufgenommen wird.
    const k = klon.querySelector<HTMLElement>('.ls-kunde');
    const fenster = klon.defaultView;
    if (k && fenster) {
      k.style.overflowWrap = 'normal';
      k.style.hyphens = 'manual';
      let groesse = parseFloat(fenster.getComputedStyle(k).fontSize);
      while (k.scrollWidth > k.clientWidth + 1 && groesse > 11) {
        groesse -= 0.5;
        k.style.fontSize = `${groesse}px`;
      }
    }
    const r = klon.querySelector<HTMLElement>('.lieferschein-rahmen');
    if (r) r.style.zoom = '1';
    const l = klon.querySelector<HTMLElement>('.lieferschein');
    if (!l) return;
    l.style.width = '210mm';
    l.style.margin = '0';
    l.style.padding = '20mm 25mm 10mm';
  });
}

export const Route = createFileRoute('/bestellungen/$bestellnr_/lieferschein')({
  validateSearch: (s: Record<string, unknown>): Suche => {
    const v = s.variante;
    return typeof v === 'string' && (VARIANTEN as string[]).includes(v)
      ? { variante: v as LieferscheinVariante }
      : {};
  },
  component: LieferscheinDruck
});
